/* istanbul ignore file */

import React from 'react';
import type {
  DeregisterValidationFn,
  FormBlurEventFn,
  FormChangeEventFn,
  RegisterValidationFn,
} from '@noths/polaris-client-react-hooks';
import { isNumber } from '@noths/polaris-client-utils';
import * as R from 'ramda';

import { Dropdown } from 'src/components/atoms/Dropdown/Dropdown';
import { TextInput } from 'src/components/atoms/TextInput/TextInput';
import {
  DIALING_CODE,
  DIALING_CODES,
} from 'src/components/organisms/Signup/constants/aboutYouOptions';
import {
  MIN_MAX_IRISH_NUMBER_ERROR,
  MIN_MAX_NUMBER_ERROR,
  MUST_NOT_BE_EMPTY_ERROR,
  PHONE_NUMBER_COPY,
} from 'src/components/organisms/Signup/constants/copy';
import { TELEPHONE_TESTID } from 'src/components/organisms/Signup/constants/testIds';
import type { FormValidationErrors } from 'src/components/organisms/Signup/types';
import * as styles from 'src/styles/styles';
import type { RegistrationForm } from 'src/types/registration';

export const MIN_IRELAND_TELEPHONE_NUMBER_LENGTH = 7;
export const MAX_IRELAND_TELEPHONE_NUMBER_LENGTH = 9;
export const MIN_TELEPHONE_NUMBER_LENGTH = 9;
export const MAX_TELEPHONE_NUMBER_LENGTH = 11;

interface Props {
  deregisterValidation: DeregisterValidationFn;
  errors: FormValidationErrors;
  formValues: RegistrationForm;
  onBlur: FormBlurEventFn;
  onFormChange: FormChangeEventFn;
  registerValidation: RegisterValidationFn<RegistrationForm>;
}

export const PhoneNumber = ({
  deregisterValidation,
  errors,
  formValues,
  onBlur,
  onFormChange,
  registerValidation,
}: Props) => {
  return (
    <>
      <Dropdown
        css={styles.dropdown}
        data-position="4.1"
        data-section="1"
        error={errors['telephone.dialingCode'] as string | undefined}
        id="trading-dialing-code"
        label={PHONE_NUMBER_COPY}
        labelHidden
        name="telephone.dialingCode"
        onBlur={onBlur}
        onChange={(e) => {
          const { value } = e.target;

          deregisterValidation('telephone.telephone');

          registerValidation('telephone.telephone', {
            validate: {
              value: (val) => {
                const telephoneValue = `${val}`.trim().length;

                if (value === DIALING_CODE.ireland) {
                  return (
                    telephoneValue < MIN_IRELAND_TELEPHONE_NUMBER_LENGTH ||
                    telephoneValue > MAX_IRELAND_TELEPHONE_NUMBER_LENGTH
                  );
                }

                return (
                  telephoneValue < MIN_TELEPHONE_NUMBER_LENGTH ||
                  telephoneValue > MAX_TELEPHONE_NUMBER_LENGTH
                );
              },
              message:
                value === DIALING_CODE.ireland ? MIN_MAX_IRISH_NUMBER_ERROR : MIN_MAX_NUMBER_ERROR,
            },
            isRequired: {
              value: true,
              message: MUST_NOT_BE_EMPTY_ERROR,
            },
          });

          onFormChange(e);
        }}
        options={DIALING_CODES}
        required
        value={formValues.telephone.dialingCode}
      />
      <TextInput
        css={styles.shortField}
        data-position="4.2"
        data-section="1"
        data-testid={TELEPHONE_TESTID}
        error={errors['telephone.telephone'] as string | undefined}
        id="trading-address-telephone"
        label=""
        name="telephone.telephone"
        onBlur={onBlur}
        onChange={(e) => {
          const { value } = e.target;
          const hasTrailingWhitespace = R.endsWith(' ', value);

          if (!isNumber(value) || hasTrailingWhitespace) {
            return;
          }

          onFormChange(e);
        }}
        placeholder="Phone number"
        required
        value={formValues.telephone.telephone}
      />
    </>
  );
};
